/**
 * Helpers shared by the listings/map browse surfaces for two features:
 *
 *   1. Saved Filters - capture the active browse-filter params under a
 *      name and re-apply them later. Stored verbatim (the same URL param
 *      shape both surfaces already use), so "apply" is just writing the
 *      params back onto the URL.
 *
 *   2. Create Alert - convert the active browse filters into the alert
 *      form's stored blob shape so the /alerts/new form can be prefilled,
 *      plus a stable key so a button can tell when the current filters
 *      already exist as an alert (and disable itself).
 *
 * Single source of truth so the list grid, the map, the saved-filter
 * dropdown, and the create-alert button never disagree on which params
 * count as a filter.
 */
import type { ReadonlyURLSearchParams } from "next/navigation";
import { decodeRings, encodeRings, polygonParamsToRings, ringsToPolygonParams } from "@/lib/draw-area";

// Pagination / sort / view / layer / one-off action params are NOT
// filters - excluded from saved-filter capture and the "any filters
// active?" check.
const NON_FILTER_KEYS = new Set([
  "page", "page_size", "sort", "layer", "view", "create_alert",
]);

// Params that may repeat in the URL (multi-select). Mirrors MULTI_KEYS in
// listing-search-params.ts.
const FILTER_MULTI_KEYS = new Set([
  "rooms", "location_ids", "city", "deal_tier", "listing_kind", "complex_slug",
  "developer", "photo_condition", "photo_has", "tags", "property_kind", "fact",
  "exclude_location_ids", "exclude_city",
]);

export type FilterParams = Record<string, string | string[]>;

/** Capture the active browse-filter params (no pagination/sort/meta) as a
 *  plain object suitable for storing as a Saved Filter. */
export function captureFilterParams(sp: ReadonlyURLSearchParams): FilterParams {
  const out: FilterParams = {};
  const seen = new Set<string>();
  for (const [k] of Array.from(sp.entries())) {
    if (NON_FILTER_KEYS.has(k) || seen.has(k)) continue;
    if (FILTER_MULTI_KEYS.has(k)) {
      const all = sp.getAll(k);
      if (all.length) out[k] = all;
    } else {
      const v = sp.get(k);
      if (v != null && v !== "") out[k] = v;
    }
    seen.add(k);
  }
  return out;
}

/** True when at least one real browse filter is set. */
export function hasActiveFilters(sp: ReadonlyURLSearchParams): boolean {
  return Object.keys(captureFilterParams(sp)).length > 0;
}

/** Stable comparison key for a filter-param object (sorted keys, sorted
 *  multi-value arrays, values stringified) so two filter sets compare
 *  equal regardless of URL ordering. */
export function filterParamsKey(params: FilterParams): string {
  const norm: Record<string, string | string[]> = {};
  for (const k of Object.keys(params).sort()) {
    const v = params[k];
    norm[k] = Array.isArray(v) ? [...v].map(String).sort() : String(v);
  }
  return JSON.stringify(norm);
}

/**
 * Convert the active browse-filter params into the blob shape the alert
 * form stores (see filterBlob in /alerts/new). Lossy on purpose:
 *   - listings `has_bill_of_sale` -> alert `has_kupcha`
 *   - `rooms` strings -> `room_counts` numbers
 *   - a radius circle (center_lat/lng/radius_km) -> alert radius mode
 *   - location_ids / complex_slug carry through verbatim: the alert form
 *     and the matcher both speak the same canonical vocabulary
 *     (build_listings_query keys on Li.location_id / Li.complex_slug), so
 *     a district/complex browse becomes a district/complex alert.
 *   - every other browse filter rides through under its stored name, so
 *     an alert matches exactly the page it was saved from (owner,
 *     2026-10-01: "if you have a filter on the listings page, you should
 *     be able to save it as an alert").
 */
export function browseParamsToAlertFilters(
  sp: ReadonlyURLSearchParams,
): Record<string, unknown> {
  const f: Record<string, unknown> = {};

  const rooms = sp.getAll("rooms").map(Number).filter((n) => !Number.isNaN(n));
  if (rooms.length) f.room_counts = rooms;

  // Listing kind, plus the two ways the magic-search parser says the
  // same thing (building_type new|old, category house). Every filter the
  // search bar can apply must be alertable (owner, 2026-09-21).
  const kinds = new Set(sp.getAll("listing_kind"));
  const bt = sp.get("building_type");
  if (bt === "new" || bt === "old") kinds.add(bt);
  if (sp.get("category") === "house") kinds.add("house");
  if (sp.get("category") === "land") kinds.add("land");
  if (kinds.size) f.listing_kind = Array.from(kinds);
  const seller = sp.get("seller_type");
  if (seller === "owner" || seller === "agent" || seller === "company") f.seller_type = seller;
  // The listings page reads no `leased` as sale and `leased=all` as both;
  // a saved alert reads no `leased` as both, so spell sale out.
  const leased = sp.get("leased");
  if (leased === "true") f.leased = true;
  else if (leased !== "all") f.leased = false;
  if (sp.get("category") === "apartment") f.category = "apartment";
  // Commercial premises and garages: the category itself (the matcher
  // never puts a deal threshold on them) and the kinds picked.
  const cat = sp.get("category");
  if (cat === "commercial" || cat === "garage") {
    f.category = cat;
    const kinds = sp.getAll("property_kind").filter(Boolean);
    if (kinds.length) f.property_kinds = kinds;
  }
  const tiers = sp.getAll("deal_tier");
  if (tiers.length) f.deal_tiers = tiers;
  const excluded = sp.getAll("exclude_tags");
  if (excluded.length) f.exclude_tags = excluded;
  const district = sp.get("district");
  if (district) f.district = district;

  // Location selections carry through as the canonical bina-parity IDs /
  // complex slugs (same params the listings URL uses), so the alert filters
  // by exactly what the user was browsing - and the daily matcher applies
  // them via the shared build_listings_query.
  const locationIds = sp.getAll("location_ids");
  if (locationIds.length) f.location_ids = locationIds;
  const complexSlugs = sp.getAll("complex_slug");
  if (complexSlugs.length) f.complex_slugs = complexSlugs;
  const developers = sp.getAll("developer");
  if (developers.length) f.developers = developers;
  // Towns outside Baku (?city=, the search bar): the stored blob key is
  // plural, a list even for one town.
  const cities = sp.getAll("city").filter(Boolean);
  if (cities.length) f.cities = cities;
  // Places and towns ruled out ("everywhere but Masazır"), lists always.
  const notPlaces = sp.getAll("exclude_location_ids").filter(Boolean);
  if (notPlaces.length) f.exclude_location_ids = notPlaces;
  const notTowns = sp.getAll("exclude_city").filter(Boolean);
  if (notTowns.length) f.exclude_cities = notTowns;

  const num = (k: string): number | null => {
    const v = sp.get(k);
    return v != null && v !== "" && !Number.isNaN(Number(v)) ? Number(v) : null;
  };
  const pmin = num("price_min"); if (pmin != null) f.price_min = pmin;
  const pmax = num("price_max"); if (pmax != null) f.price_max = pmax;
  const amin = num("area_min"); if (amin != null) f.area_min = amin;
  const amax = num("area_max"); if (amax != null) f.area_max = amax;
  const flmin = num("floor_min"); if (flmin != null) f.floor_min = flmin;
  const flmax = num("floor_max"); if (flmax != null) f.floor_max = flmax;
  const ppmin = num("ppm_min"); if (ppmin != null) f.ppm_min = ppmin;
  const ppmax = num("ppm_max"); if (ppmax != null) f.ppm_max = ppmax;
  const tfmin = num("total_floors_min"); if (tfmin != null) f.total_floors_min = tfmin;
  const tfmax = num("total_floors_max"); if (tfmax != null) f.total_floors_max = tfmax;
  const myield = num("min_yield"); if (myield != null) f.min_yield = myield;

  // Photo-verified renovation tiers. Browse param is the singular
  // `photo_condition` (repeated); the stored blob key is plural.
  const photoConds = sp.getAll("photo_condition");
  if (photoConds.length) f.photo_conditions = photoConds;
  // "Seen in the photos" stays out of alerts (owner, 2026-10-22): new
  // listings get their photo tags later than the alert looks back.

  // The search bar's own filters (owner, 2026-09-20: "saving filters must
  // take the search features into account"): amenity tags and the text
  // search. The matcher applies both through build_listings_query.
  const tags = sp.getAll("tags");
  if (tags.length) f.feature_tags = tags;
  // What the listing's own text says (lib/text-facts): the stored key is
  // plural, like the tags.
  const facts = sp.getAll("fact").filter(Boolean);
  if (facts.length) f.facts = facts;
  const q = sp.get("q");
  if (q && q.trim()) f.q = q.trim().slice(0, 120);
  // Rent period (daily / room / all; monthly is the default) and the
  // walk to the metro.
  const period = sp.get("rent_period");
  if (period === "daily" || period === "room" || period === "all") f.rent_period = period;
  const walk = num("metro_walk"); if (walk != null) f.metro_walk = walk;

  const tri = (k: string): "yes" | "no" | null => {
    const v = sp.get(k);
    return v === "yes" || v === "no" ? v : null;
  };
  const rep = tri("has_repair"); if (rep) f.has_repair = rep;
  const mtk = tri("mortgage_terms_known"); if (mtk) f.mortgage_terms_known = mtk;
  const mor = tri("has_mortgage"); if (mor) f.has_mortgage = mor;
  const deed = tri("has_bill_of_sale"); if (deed) f.has_kupcha = deed;

  // Floor-position toggles ride through to the alert (matcher honours them).
  if (sp.get("exclude_first_floor") === "true") f.exclude_first_floor = true;
  if (sp.get("exclude_last_floor") === "true") f.exclude_last_floor = true;

  // Drawn map areas: the URL keeps them packed (`draw`), the alert blob
  // stores the API's `polygon` rings the matcher reads directly.
  const polygon = ringsToPolygonParams(decodeRings(sp.get("draw")));
  if (polygon.length) f.polygon = polygon;

  // The loan's rate range under the take-over filter, in percent.
  const rmin = num("mortgage_rate_min"); if (rmin != null) f.mortgage_rate_min = rmin;
  const rmax = num("mortgage_rate_max"); if (rmax != null) f.mortgage_rate_max = rmax;
  const years = sp.getAll("mortgage_years").filter(Boolean);
  if (years.length) f.mortgage_years = years;
  // "Listed since": Baku calendar days, kept as the URL writes them.
  for (const k of ["listed_after", "listed_before"] as const) {
    const v = sp.get(k);
    if (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) f[k] = v;
  }

  // Everything else rides through under the stored name (the backend
  // FilterPayload mirrors listing_filters_dep; tests/test_alert_listing_parity
  // fails when a listings filter has no alert field), so a filter added to
  // the listings page can never be dropped here by omission.
  const RENAME: Record<string, string> = {
    metro: "metros", sub_location: "sub_locations",
  };
  const HANDLED = new Set([
    "rooms", "listing_kind", "building_type", "category", "seller_type", "leased", "deal_tier",
    "exclude_tags", "district", "location_ids", "city", "complex_slug", "developer", "price_min", "price_max",
    "area_min", "area_max", "floor_min", "floor_max", "ppm_min", "ppm_max", "total_floors_min",
    "total_floors_max", "min_yield", "photo_condition", "photo_has", "tags", "q", "rent_period", "metro_walk",
    "has_repair", "has_mortgage", "has_bill_of_sale", "exclude_first_floor", "exclude_last_floor",
    "center_lat", "center_lng", "radius_km", "draw", "mortgage_rate_min", "mortgage_rate_max", "mortgage_terms_known", "mortgage_years", "listed_after", "listed_before",
    "property_kind", "fact", "exclude_location_ids", "exclude_city",
    // Browse-only state, not a filter.
    "page", "page_size", "sort", "open",
  ]);
  for (const key of new Set(Array.from(sp.keys()))) {
    if (HANDLED.has(key)) continue;
    const vals = sp.getAll(key).filter((v) => v !== "");
    if (!vals.length) continue;
    const name = RENAME[key] ?? key;
    f[name] = key === "metro" || key === "sub_location" || key === "districts" || vals.length > 1 ? vals : vals[0];
  }

  const lat = num("center_lat");
  const lng = num("center_lng");
  const rad = num("radius_km");
  if (lat != null && lng != null && rad != null) {
    f.geo_mode = "radius";
    f.center_lat = lat;
    f.center_lng = lng;
    f.radius_km = rad;
  } else {
    f.geo_mode = "area";
  }
  return f;
}

/** Stable key for an alert filter blob (same normalisation as
 *  filterParamsKey) so the converted current filters can be compared to
 *  an existing alert's stored filters for exact-match detection. */
export function alertFilterKey(filters: Record<string, unknown>): string {
  const norm: Record<string, unknown> = {};
  for (const k of Object.keys(filters).sort()) {
    const v = filters[k];
    if (v == null) continue;
    norm[k] = Array.isArray(v) ? v.map(String).sort() : String(v);
  }
  return JSON.stringify(norm);
}

/** Base64-encode the prefill blob for the /alerts/new?prefill= param.
 *  encodeURIComponent first so non-ASCII (none today, but safe) survives
 *  btoa. */
export function encodePrefill(filters: Record<string, unknown>): string {
  return btoa(encodeURIComponent(JSON.stringify(filters)));
}

/** Inverse of encodePrefill; returns null on any malformed input. */
export function decodePrefill(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(decodeURIComponent(atob(raw)));
    return obj && typeof obj === "object" ? obj : null;
  } catch {
    return null;
  }
}

/** An alert's stored blob -> the listings page's URL params: what the
 *  alert form's search bar reads and what the Telegram Mini App's browse
 *  screen opens with to edit an alert's filters. The inverse of
 *  browseParamsToAlertFilters. */
export function alertBlobToBrowseParams(blob: Record<string, unknown>): URLSearchParams {
  const NAME: Record<string, string> = {
    room_counts: "rooms", feature_tags: "tags", deal_tiers: "deal_tier", developers: "developer",
    photo_conditions: "photo_condition", complex_slugs: "complex_slug", metros: "metro", cities: "city",
    sub_locations: "sub_location", has_kupcha: "has_bill_of_sale",
    property_kinds: "property_kind", facts: "fact",
  };
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(blob)) {
    if (v == null || v === "" || k === "geo_mode") continue;
    if (k === "leased") continue;
    if (k === "polygon") {
      // The alert stores the API rings; the browse URL packs them.
      const draw = encodeRings(polygonParamsToRings(Array.isArray(v) ? v.map(String) : [String(v)]));
      if (draw) sp.set("draw", draw);
      continue;
    }
    const key = NAME[k] ?? k;
    if (Array.isArray(v)) v.forEach((x) => sp.append(key, String(x)));
    else sp.set(key, String(v));
  }
  if (blob.leased === true) sp.set("leased", "true");
  else if (blob.leased !== false) sp.set("leased", "all");
  return sp;
}
